/* eslint-disable */
import React, { useContext, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*------ Import API utilities ------*/
import * as url from "helper/UrlHelper";
import { getData } from "utils/Gateway";

/**
 * ViewResponsePopup Component
 *
 * A comprehensive survey response viewer that displays survey questions and their answers
 * in an attractive offcanvas popup. Supports multiple question types including ratings,
 * single/multi-select, and text responses.
 *
 * @param {string} viewInviteId - Unique identifier for the survey invite
 * @param {function} setViewInviteId - State setter to manage invite ID
 */
const ViewResponsePopup = ({ viewInviteId, setViewInviteId }) => {
  // ========== CONSTANTS & CONFIGURATION ==========

  /** Rating scale configuration for star-based questions */
  const RATING_SCALE = [1, 2, 3, 4, 5];

  /** Translation hook for internationalization */
  const { t } = useTranslation();

  // ========== STATE MANAGEMENT ==========

  /**
   * Survey questions and answers data
   * Contains question details, types, and user responses
   */
  const [questionList, setQuestionList] = useState([]);

  /**
   * Total number of survey responses received
   * Used to determine if survey has been completed
   */
  const [surveyResponseCount, setSurveyResponseCount] = useState(0);

  /**
   * Loading state for API operations
   * Provides user feedback during data fetching
   */
  const [isLoading, setIsLoading] = useState(false);

  // ========== API FUNCTIONS ==========

  /**
   * Fetches comprehensive survey invite details including questions and responses
   *
   * Retrieves:
   * - Survey question list with types and configurations
   * - User responses for each question
   * - Response count and completion status
   *
   * @async
   * @function getSurveyInviteDetails
   */
  const getSurveyInviteDetails = async () => {
    if (!viewInviteId) return;

    try {
      setIsLoading(true);

      const requestURL = `${url.API_BASE_URL}${url.API_GET_SURVEY_INVITE_DETAILS}/${viewInviteId}`;

      console.log("Fetching survey details from:", requestURL);

      const response = await getData(requestURL);

      console.log("Survey details response:", response);

      if (response.status) {
        setQuestionList(response.data.questionlist || []);
        setSurveyResponseCount(response.data.surveyresponses?.length || 0);
      } else {
        console.warn("Failed to fetch survey details:", response.message);
      }
    } catch (error) {
      console.error("Error fetching survey invite details:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== EVENT HANDLERS ==========

  /**
   * Handles popup closure and state cleanup
   *
   * Resets all component state to initial values and closes the offcanvas
   * Ensures clean state for subsequent usage
   */
  const handlePopupClose = () => {
    setQuestionList([]);
    setSurveyResponseCount(0);
    setViewInviteId(null);
  };

  // ========== RENDER HELPERS ==========

  /**
   * Renders star rating display for rating-type questions
   *
   * @param {string} rating - The numeric rating value (1-5)
   * @returns {JSX.Element} Star rating component
   */
  const renderStarRating = (rating) => {
    const ratingValue = parseInt(rating);

    return (
      <div className="rating-display d-flex align-items-center gap-1">
        <ul className="d-flex list-unstyled mb-0">
          {RATING_SCALE.map((starIndex) => (
            <li key={starIndex}>
              <span
                className={`d-block material-symbols-outlined fs-5 ${
                  starIndex <= ratingValue
                    ? "text-warning icon-fill"
                    : "text-muted"
                }`}
                title={`${starIndex} star${starIndex !== 1 ? "s" : ""}`}
              >
                star
              </span>
            </li>
          ))}
        </ul>
        <span className="ms-2 fs-sm text-muted">({ratingValue}/5)</span>
      </div>
    );
  };

  /**
   * Renders single selection answer display
   *
   * @param {string} answer - The selected option
   * @returns {JSX.Element} Single select answer component
   */
  const renderSingleSelect = (answer) => (
    <div className="single-select-answer pt-1">
      <span className="px-3 py-2 fs-sm border border-gray-300 rounded-3">
        {answer}
      </span>
    </div>
  );

  /**
   * Renders multiple selection answers display
   *
   * @param {string} answers - Comma-separated selected options
   * @returns {JSX.Element} Multi-select answers component
   */
  const renderMultiSelect = (answers) => {
    const answerArray = answers.split(", ").filter((answer) => answer.trim());

    return (
      <div className="multi-select-answers d-flex flex-wrap gap-2">
        {answerArray.map((answer, index) => (
          <span
            key={index}
            className="px-3 py-2 fs-sm border border-gray-300 rounded-3"
          >
            {answer.trim()}
          </span>
        ))}
      </div>
    );
  };

  /**
   * Renders text-based answer display
   *
   * @param {string} answer - The text response
   * @returns {JSX.Element} Text answer component
   */
  const renderTextAnswer = (answer) => (
    <div className="text-answer p-3 bg-light rounded-10 border border-gray-200">
      <p className="mb-0 fs-sm text-dark">{answer}</p>
    </div>
  );

  /**
   * Renders appropriate answer component based on question type
   *
   * @param {Object} question - Question object with type and answer
   * @returns {JSX.Element|null} Rendered answer component
   */
  const renderQuestionAnswer = (question) => {
    const { questiontypeslug, surveyanswer } = question;

    // Skip rendering if no answer provided
    if (!surveyanswer || surveyanswer.trim() === "") {
      return null;
    }

    switch (questiontypeslug) {
      case "RATING":
        return renderStarRating(surveyanswer);
      case "SINGLE SELECT":
        return renderSingleSelect(surveyanswer);
      case "MULTI SELECT":
        return renderMultiSelect(surveyanswer);
      case "TEXT":
      case "COMMENTS":
        return renderTextAnswer(surveyanswer);
      default:
        return <p className="fs-sm text-muted">Unsupported question type</p>;
    }
  };

  // ========== LIFECYCLE EFFECTS ==========

  /**
   * Effect: Fetch survey details when viewInviteId changes
   *
   * Triggers API call to load survey data whenever a new invite ID is provided
   */
  useEffect(() => {
    if (viewInviteId) {
      getSurveyInviteDetails();
    }
  }, [viewInviteId]);

  return (
    <div
      className="offcanvas md offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="offcanvasViewResponse"
      aria-labelledby="offcanvasViewResponseLabel"
    >
      {/* ========== POPUP HEADER ========== */}
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              quiz
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasViewResponseLabel"
            >
              {t("Survey Response")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {surveyResponseCount > 0
                ? t("Response received and processed")
                : t("Awaiting participant response")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={handlePopupClose}
        ></button>
      </div>

      {/* ========== POPUP BODY ========== */}
      <div className="offcanvas-body p-4">
        {/* Loading State */}
        {isLoading && (
          <div className="loading-state text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">{t("Loading survey responses...")}</p>
          </div>
        )}

        {/* No Response State */}
        {!isLoading && surveyResponseCount === 0 && (
          <div className="no-response-state text-center py-5">
            <div
              className="icon-wrapper p-4 bg-warning-subtle rounded-circle mx-auto mb-3"
              style={{ width: "80px", height: "80px" }}
            >
              <span className="material-symbols-outlined text-warning fs-1">
                schedule
              </span>
            </div>
            <h5 className="fw-semibold text-dark mb-2">
              {t("Awaiting Response")}
            </h5>
            <p className="text-muted fs-sm mb-0">
              {t(
                "The participant hasn't submitted their response yet. Please check back later."
              )}
            </p>
          </div>
        )}

        {/* Survey Questions and Responses */}
        {!isLoading && surveyResponseCount > 0 && (
          <form
            onSubmit={(e) => e.preventDefault()}
            className="survey-responses"
          >
            <div className="responses-header mb-4 p-3 bg-success-subtle rounded-10">
              <div className="d-flex align-items-center gap-2">
                <span className="material-symbols-outlined text-success">
                  check_circle
                </span>
                <span className="fw-semibold text-success">
                  {t("Response Completed")}
                </span>
              </div>
            </div>

            {/* Question List */}
            <div className="questions-container">
              {questionList.map((question, index) => {
                // Skip questions without answers
                if (
                  !question.surveyanswer ||
                  question.surveyanswer.trim() === ""
                ) {
                  return null;
                }

                return (
                  <div
                    key={index}
                    className="question-item mb-4 p-4 border border-gray-200 rounded-10 bg-white shadow-sm"
                  >
                    {/* Question Header */}
                    <div className="question-header mb-3">
                      <div className="d-flex align-items-start gap-3">
                        <span
                          className="question-number badge bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: "24px",
                            height: "24px",
                            fontSize: "0.75rem",
                          }}
                        >
                          {index + 1}
                        </span>
                        <div className="flex-grow-1">
                          <h6 className="question-text fw-semibold text-dark mb-1">
                            {question.name}
                          </h6>
                          <span className="question-type badge bg-secondary-subtle text-secondary fs-xs my-1">
                            {question.questiontypeslug?.replace("_", " ")}
                          </span>
                          {/* Question Answer */}
                          <div className="question-answer mt-2">
                            {renderQuestionAnswer(question)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </form>
        )}

        {/* Action Button */}
        {viewInviteId && (
          <div className="action-section mt-4 pt-3 border-top border-gray-300">
            <Link
              to={`/admin/survey/invite/${viewInviteId}`}
              target="_blank"
              className="btn btn-primary w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
              rel="noopener noreferrer"
            >
              <span className="material-symbols-outlined me-2">
                open_in_new
              </span>
              <span>{t("View Full Survey")}</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewResponsePopup;
